import { store, getContext, getElement } from '@wordpress/interactivity';

// Helper function to measure viewport bounds
function measureViewportBounds(sliderElement) {
	// Create a temporary element with the same alignment as the viewport
	const viewport = sliderElement.querySelector('.wp-block-haptiq-slider-viewport');
	const tempElement = document.createElement('div');
	
	// Copy ALL classes from viewport, not just alignment ones
	tempElement.className = viewport.className;
	
	// Add some content to ensure it has dimensions
	tempElement.innerHTML = '<div style="height: 20px;">temp</div>';
	
	// Make it invisible but measurable (don't use absolute positioning)
	tempElement.style.cssText = `
		visibility: hidden;
		pointer-events: none;
		position: relative;
	`;
	
	// Insert it right before the slider to get the same layout context
	sliderElement.parentNode.insertBefore(tempElement, sliderElement);
	
	// Measure the bounds
	const bounds = tempElement.getBoundingClientRect();
	
	// Clean up
	tempElement.remove();
	
	console.log('Temp element classes:', tempElement.className);
	console.log('Measured bounds:', bounds);
	
	return {
		left: bounds.left,
		right: bounds.right,
		width: bounds.width
	};
}

// Helper function to update slide status attributes
function updateSlideStatus(sliderElement, context) {
	const viewport = sliderElement.querySelector('.wp-block-haptiq-slider-viewport');
	if (!viewport) return;
	
	const children = Array.from(viewport.children);
	if (children.length === 0) return;
	
	// Use measured viewport bounds for accurate detection
	const bounds = context.viewportBounds;
	const browserWindow = {
		left: 0,
		right: window.innerWidth,
		top: 0,
		bottom: window.innerHeight
	};

	let snappedSlideIndex = 0;
	let minLeftDistance = Infinity;
	
	// Get scroll-padding from viewport wrapper
	const viewportWrapper = viewport.closest('.wp-block-haptiq-slider-viewport-wrapper');
	const scrollPaddingLeft = viewportWrapper ? parseFloat(getComputedStyle(viewportWrapper).scrollPaddingLeft) || 0 : 0;
	const snapPoint = bounds.left + scrollPaddingLeft;
	
	console.log('Scroll padding left:', scrollPaddingLeft, 'Snap point:', snapPoint, 'Bounds left:', bounds.left);
	
	// Analyze each slide and update their status
	children.forEach((child, i) => {
		const rect = child.getBoundingClientRect();
		
		// Browser window visibility
		const browserVisible = rect.left < browserWindow.right && rect.right > browserWindow.left &&
		                      rect.top < browserWindow.bottom && rect.bottom > browserWindow.top;
		const browserFullyVisible = rect.left >= browserWindow.left && rect.right <= browserWindow.right &&
		                            rect.top >= browserWindow.top && rect.bottom <= browserWindow.bottom;
		
		// Viewport containment (using measured bounds)
		const viewportVisible = rect.left < bounds.right && rect.right > bounds.left;
		const viewportFullyVisible = rect.left >= bounds.left && rect.right <= bounds.right;
		
		// Find snapped slide (closest left edge to viewport left, accounting for scroll-padding)
		const leftDistance = Math.abs(rect.left - snapPoint);
		if (leftDistance < minLeftDistance) {
			minLeftDistance = leftDistance;
			snappedSlideIndex = i;
		}
		
		// Calculate visibility states
		let visibleState = 'hidden';
		if (browserFullyVisible) visibleState = 'full';
		else if (browserVisible) visibleState = 'partial';
		
		let viewportState = 'outside';
		if (viewportFullyVisible) viewportState = 'full';
		else if (viewportVisible) viewportState = 'partial';
		
		// Store in context for button states
		if (i === 0) context.firstSlideVisible = viewportVisible;
		if (i === children.length - 1) context.lastSlideVisible = viewportVisible;
		
		// Set data attributes
		child.setAttribute('data-visible', visibleState);
		child.setAttribute('data-in-viewport', viewportState);
		child.setAttribute('data-snapped', i === snappedSlideIndex);
		
		console.log(`Slide ${i}:`, {
			visible: visibleState,
			inViewport: viewportState,
			snapped: i === snappedSlideIndex,
			leftDistance
		});
	});

	// Update snapped slide
	children.forEach((child, i) => {
		child.setAttribute('data-snapped', i === snappedSlideIndex);
	});

	console.log('Snapped slide index:', snappedSlideIndex, 'Current:', context.currentSlide);
	
	return snappedSlideIndex;
}

const { state } = store('haptiq/slider', {
	state: {
		get isFirst() {
			// Disable previous button if first slide is visible in viewport
			return getContext().firstSlideVisible || false;
		},
		get isLast() {
			// Disable next button if last slide is visible in viewport
			return getContext().lastSlideVisible || false;
		},
	},
	actions: {
		next() {
			const context = getContext();
			console.log('Next button clicked, current:', context.currentSlide, 'total:', context.totalSlides);
			if (context.currentSlide < context.totalSlides - 1) {
				context.currentSlide += 1;
				console.log('Moving to slide:', context.currentSlide);
				// Trigger scroll manually
				const { ref } = getElement();
				// Button is inside controls, need to find the slider container
				const sliderContainer = ref.closest('.wp-block-haptiq-slider');
				const viewport = sliderContainer?.querySelector('.wp-block-haptiq-slider-viewport');
				const target = viewport?.children[context.currentSlide];
				console.log('Viewport:', viewport, 'Target:', target);
				if (target) {
					context.isScrolling = true;
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'start',
					});
					setTimeout(() => {
						context.isScrolling = false;
					}, 500);
				}
			}
		},
		previous() {
			const context = getContext();
			if (context.currentSlide > 0) {
				context.currentSlide -= 1;
				// Trigger scroll manually
				const { ref } = getElement();
				// Button is inside controls, need to find the slider container
				const sliderContainer = ref.closest('.wp-block-haptiq-slider');
				const viewport = sliderContainer?.querySelector('.wp-block-haptiq-slider-viewport');
				const target = viewport?.children[context.currentSlide];
				if (target) {
					context.isScrolling = true;
					target.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'start',
					});
					setTimeout(() => {
						context.isScrolling = false;
					}, 500);
				}
			}
		},
	},
	callbacks: {
		initSlider() {
			const { ref } = getElement();
			const viewport = ref.querySelector('.wp-block-haptiq-slider-viewport');
			if (viewport) {
				// Count all direct children as slides
				const context = getContext();
				context.totalSlides = viewport.children.length;
				context.isScrolling = false; // Flag to prevent scroll feedback loops
				
				// Measure the true viewport area using a temporary element
				context.viewportBounds = measureViewportBounds(ref);
				console.log('Measured viewport bounds:', context.viewportBounds);
				
				// Initialize slide status attributes
				updateSlideStatus(ref, context);
				
				console.log('Total slides found:', viewport.children.length);
				console.log('Viewport children:', Array.from(viewport.children).map(c => c.className));
			} else {
				console.log('No viewport found in:', ref);
				console.log('Available elements:', ref.querySelectorAll('*'));
			}
		},
		scrollToCurrent() {
			const context = getContext();
			
			// Don't scroll if we're already in a scroll operation (prevents feedback loop)
			if (context.isScrolling) return;
			
			const { ref } = getElement();
			const viewport = ref.querySelector('.wp-block-haptiq-slider-viewport');
			
			const target = viewport?.children[context.currentSlide];
			if (!target) return;

			// Check if target is already in the correct position
			const viewportRect = viewport.getBoundingClientRect();
			const targetRect = target.getBoundingClientRect();
			const tolerance = 5; // Allow small differences
			
			if (Math.abs(targetRect.left - viewportRect.left) < tolerance) {
				// Already in position, no need to scroll
				return;
			}

			context.isScrolling = true;
			
			target.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'start',
			});
			
			// Reset flag after scroll completes
			setTimeout(() => {
				context.isScrolling = false;
			}, 500);
		},
		onScrollEnd() {
			const { ref } = getElement();
			const viewport = ref.querySelector('.wp-block-haptiq-slider-viewport');
			if (!viewport) {
				console.log('onScrollEnd: No viewport found');
				return;
			}

			const context = getContext();
			const children = Array.from(viewport.children);
			if (children.length === 0) {
				console.log('onScrollEnd: No children found');
				return;
			}

			console.log('onScrollEnd: isScrolling =', context.isScrolling);

			// Update slide status and get snapped slide index
			const snappedSlideIndex = updateSlideStatus(ref, context);

			// Only update if different and we're not programmatically scrolling
			if (snappedSlideIndex !== context.currentSlide && !context.isScrolling) {
				console.log('onScrollEnd: Updating currentSlide to', snappedSlideIndex);
				context.currentSlide = snappedSlideIndex;
			}
		}
	}
});